import { Button, Flex, Loader, Menu, MenuButton, Table, TableBody, TableCell, TableHead, TableRow } from '@aws-amplify/ui-react';
import { useState } from 'react';
import { AiFillWarning, AiOutlineArrowDown, AiOutlineArrowRight, AiOutlineMail } from 'react-icons/ai';
import { FaEllipsisH, FaExternalLinkAlt } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { getHubspotUrl } from '../utils/CrmUtils';
import General from '../utils/GeneralUtils';
import './ExportsTable.scss';
import MetricValue from './MetricValue';
import NA from './NA';
import UniversalTag from './UniversalTag';

// Statuses that cannot be archived (only successful completions)
const NON_ARCHIVABLE_STATUSES = ['SUCCESS', 'COMPLETED'];

export interface Export {
  id: string;
  created_at: string;
  completed_at?: string;
  entity: string;
  target?: string;
  version: number;
  user_email?: string;
  record_count?: number;
  success_count?: number;
  failed_count?: number;
  skipped_count?: number;
  status: string;
  error?: string;
  file_name?: string;
  file_size?: number;
  has_file?: boolean;
  has_report?: boolean;
  metadata?: any;
  stats?: {
    hubspotListId?: string;
    hubspotPortalId?: string;
    hubspotListName?: string;
    hubspotContactsAddedToList?: number;
    [key: string]: any;
  };
}

interface ExportsTableProps {
  exports: Export[];
  onDownload?: (ex: Export) => void;
  onDownloadReport?: (ex: Export) => void;
  onArchive?: (ex: Export) => void;
  onCopy?: (ex: Export) => void;
  onViewDetail?: (exportId: string) => void;
  /** When true, hides action buttons like Copy and Archive (for admin read-only view) */
  readOnly?: boolean;
}

export default function ExportsTable({ exports, onDownload, onDownloadReport, onArchive, onCopy, onViewDetail, readOnly }: ExportsTableProps) {
  const navigate = useNavigate();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  function getDuration(ex: Export) {
    if (!ex.created_at || !ex.completed_at) {
      return {
        durationSec: 0,
        duration: <NA />
      };
    }
    
    const startTime = new Date(ex.created_at).getTime();
    const endTime = new Date(ex.completed_at).getTime();
    const durationMs = endTime - startTime;
    
    // Convert to seconds
    const durationSec = Math.floor(durationMs / 1000);
    
    if (durationSec < 60) {
      return {
        durationSec,
        duration: <>{durationSec}s</>
      };
    } else {
      const minutes = Math.floor(durationSec / 60);
      const seconds = durationSec % 60;
      return {
        durationSec,
        duration: <>{minutes}m {seconds}s</>
      };
    }
  }

  return (
    <Table size="small" className="exports-table">
      <TableHead>
        <TableRow>
          <TableCell as='th'>Date</TableCell>
          <TableCell as='th'>Type</TableCell>
          <TableCell as='th'>Exported By</TableCell>
          <TableCell as='th'>Nr. of Records</TableCell>
          <TableCell as='th'>Duration</TableCell>
          <TableCell as='th'>Status</TableCell>
          <TableCell as='th'></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {
          exports.map((ex: Export, i: number) => {
            const duration = getDuration(ex);
            const isV2 = ex.version === 2;
            // 'REJECTED' is shared with decision/canonical-contact statuses, which style it
            // red. An export rejection is a bad client request, not a failure - show it yellow.
            const statusClass = ex.status === 'REJECTED' ? 'universal-tag-export-rejected' : '';
            return (
              <TableRow
                key={i}
                className={isV2 ? 'exports-table__row--clickable' : ''}
                onClick={isV2 ? () => navigate(`/exports/${ex.id}`) : undefined}
              >
                <TableCell>
                  <span
                    data-tooltip-content={General.formatDateTime(ex.created_at)}
                    data-tooltip-id="main-tooltip"
                  >
                    {General.daysAgo(ex.created_at)}
                  </span>
                </TableCell>
                <TableCell>
                  <Flex alignItems='center' gap={5}>
                    <UniversalTag category={ex.entity} />
                    {ex.target && (
                      <>
                        <AiOutlineArrowRight size={12} className="exports-table__arrow-icon" />
                        <UniversalTag category={ex.target} />
                      </>
                    )}
                  </Flex>
                </TableCell>
                <TableCell>{ex.user_email || <NA />}</TableCell>
                <TableCell>
                  <MetricValue
                    type='NR_EXPORTED_RECORDS'
                    value={ex.record_count || 0}
                    displayComponent={
                      ex.record_count ? (
                        <Flex alignItems="center" gap={4}>
                          <span>{General.addCommasToNumber(ex.record_count)}</span>
                          {((ex.failed_count && ex.failed_count > 0) || (ex.skipped_count && ex.skipped_count > 0)) ? (
                            <span
                              data-tooltip-id="main-tooltip"
                              data-tooltip-html={`<div style="padding: 5px 0;"><div>${ex.success_count || 0} succeeded, ${ex.failed_count || 0} failed, ${ex.skipped_count || 0} skipped</div><div style="margin-top: 5px;" class="desc-2">Download the report to see the details.</div></div>`}
                              className="exports-table__warning-icon"
                            >
                              <AiFillWarning size={16} />
                            </span>
                          ) : null}
                        </Flex>
                      ) : (
                        <NA />
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <MetricValue 
                    type='EXPORT_DURATION'
                    value={duration.durationSec}
                    displayComponent={duration.duration}
                  />
                </TableCell>
                <TableCell>
                  <Flex gap={8} alignItems="center">
                    {
                      ex.status === 'FAILURE' || ex.status === 'REJECTED'
                        ? <span
                            data-tooltip-hidden={!ex?.error}
                            data-tooltip-id="main-tooltip"
                            data-tooltip-content={ex.error}
                            className="exports-table__error-tooltip"
                          >
                            <UniversalTag category={ex.status} classOverride={statusClass} />
                          </span>
                        : <UniversalTag category={ex.status} classOverride={statusClass} />
                    }
                    {
                      (ex.status === 'PENDING' || ex.status === 'IN_PROGRESS') &&
                        <Loader size="small" />
                    }
                  </Flex>
                </TableCell>
                <TableCell>
                  <Flex gap={10} justifyContent="flex-end" alignItems="center">
                    {
                      ex.metadata?.revalidateEmailsProvider && ex.metadata.revalidateEmailsProvider !== 'NONE' &&
                        <span
                          data-tooltip-id="main-tooltip"
                          data-tooltip-content={`Emails revalidated with ${ex.metadata.revalidateEmailsProvider === 'ZEROBOUNCE' ? 'zerobounce.net' : 'neverbounce.com'}`}
                          className="exports-table__email-icon"
                        >
                          <AiOutlineMail size={16} />
                        </span>
                    }
                    {
                      ex.status === 'SUCCESS' && ex.stats?.hubspotListId && ex.stats?.hubspotPortalId &&
                        <Link
                          to={getHubspotUrl(ex.stats.hubspotPortalId, 'list', ex.stats.hubspotListId) || '#'}
                          target="_blank"
                          style={{ fontSize: 12 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FaExternalLinkAlt size={10} style={{ marginRight: 2 }} /> View HubSpot List
                        </Link>
                    }
                    {
                      ex.status === 'SUCCESS' && ex.has_file && onDownload &&
                        <Button
                          size='small'
                          className='tiniest'
                          onClick={(e) => { e.stopPropagation(); onDownload(ex); }}
                        >
                          <AiOutlineArrowDown size={10} className="exports-table__download-icon" /> Download
                        </Button>
                    }
                    {
                      ex.status === 'SUCCESS' && ex.has_report && onDownloadReport &&
                        <Button
                          size='small'
                          className='tiniest'
                          onClick={(e) => { e.stopPropagation(); onDownloadReport(ex); }}
                        >
                          <AiOutlineArrowDown size={10} className="exports-table__download-icon" /> Download Report
                        </Button>
                    }
                    {!readOnly && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Menu
                          size="small"
                          isOpen={openMenuId === ex.id}
                          onOpenChange={(isOpen) => setOpenMenuId(isOpen ? ex.id : null)}
                          trigger={
                            <MenuButton size="small" className="tiniest nohover">
                              <FaEllipsisH />
                            </MenuButton>
                          }
                        >
                          {isV2 && (
                            <Button
                              className="menu-item"
                              onClick={() => {
                                setOpenMenuId(null);
                                onCopy?.(ex);
                              }}
                            >
                              Copy to New
                            </Button>
                          )}
                          <Button
                            className="menu-item"
                            isDisabled={NON_ARCHIVABLE_STATUSES.includes(ex.status)}
                            onClick={() => {
                              setOpenMenuId(null);
                              onArchive?.(ex);
                            }}
                          >
                            Archive
                          </Button>
                        </Menu>
                      </div>
                    )}
                  </Flex>
                </TableCell>
              </TableRow>
            )
          })
        }
      </TableBody>
    </Table>
  );
}